import { FilestackImageFit } from 'api/types/shared.types';
import { getFilestackImageSize } from 'components/Image/Filestack/getFilestackImageSize';
import { getImageSrc } from 'utils/getImageSrc';

import {
  filestackLoadingSrcBlur,
  filestackLoadingSrcQuality,
  filestackLoadingSrcRatio,
} from './filestackLoadingSrcConstants';

type Props = {
  handle: string | undefined;
  mimetype: string | undefined;
  quality: number;
  format: string | undefined;
  fit: FilestackImageFit | undefined;
  originalWidth: number | undefined;
  originalHeight: number | undefined;
  requestedWidth: number | undefined;
  requestedHeight: number | undefined;
  maxWidth: number | undefined;
  maxHeight: number | undefined;
};

export function getFilestackImageProps({
  handle,
  mimetype,
  quality,
  format,
  fit,
  originalWidth,
  originalHeight,
  requestedWidth,
  requestedHeight,
  maxWidth,
  maxHeight,
}: Props):
  | {
      loadingSrc: string;
      src: string;
      srcSet: string | undefined;
      size: { width: number | undefined; height: number | undefined };
    }
  | undefined {
  if (!handle) return undefined;

  const size = getFilestackImageSize({
    originalWidth,
    originalHeight,
    requestedWidth,
    requestedHeight,
    maxWidth,
    maxHeight,
  });

  const size2x = getFilestackImageSize({
    originalWidth,
    originalHeight,
    maxWidth: size.width ? size.width * 2 : undefined,
    maxHeight: size.height ? size.height * 2 : undefined,
  });

  const has2xWidth =
    size.width && size2x.width ? size2x.width === size.width * 2 : false;

  const has2xHeight =
    size.height && size2x.height ? size2x.height === size.height * 2 : false;

  const has2xSize = has2xWidth || has2xHeight;

  const loadingSrc = getImageSrc({
    handle,
    mimetype,
    quality: filestackLoadingSrcQuality,
    format,
    fit,
    blur: filestackLoadingSrcBlur,
    width: size.width ? size.width * filestackLoadingSrcRatio : undefined,
    height: size.height ? size.height * filestackLoadingSrcRatio : undefined,
  });

  const src = getImageSrc({
    handle,
    mimetype,
    quality,
    format,
    fit,
    width: size.width,
    height: size.height,
  });

  const src2x = has2xSize
    ? getImageSrc({
        handle,
        mimetype,
        quality,
        format,
        fit,
        width: size2x.width,
        height: size2x.height,
      })
    : undefined;

  const srcSet = src2x
    ? [src, src2x].map((str, index) => `${str} ${index + 1}x`).join(', ')
    : undefined;

  return { loadingSrc, src, srcSet, size };
}
